/**
 * Wishlist — localStorage-based favourites
 * Stores [{id, handle}] per product.
 * Product page renders via /products/{handle}.js
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'fox_wishlist';

  /* ── Storage helpers ───────────────────────────────────────────── */
  function getItems() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      // Support legacy format (plain id strings)
      return parsed.map(item =>
        typeof item === 'object' ? item : { id: String(item), handle: null }
      );
    } catch {
      return [];
    }
  }

  function saveItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }

  function getIds() {
    return getItems().map(i => String(i.id));
  }

  function isInWishlist(id) {
    return getIds().includes(String(id));
  }

  function addItem(id, handle) {
    const items = getItems();
    const sid = String(id);
    if (!items.find(i => String(i.id) === sid)) {
      items.push({ id: sid, handle: handle || null });
      saveItems(items);
    }
  }

  function removeItem(id) {
    const sid = String(id);
    saveItems(getItems().filter(i => String(i.id) !== sid));
  }

  function toggleWishlist(id, handle) {
    if (isInWishlist(id)) {
      removeItem(id);
      return false; // removed
    } else {
      addItem(id, handle);
      return true; // added
    }
  }

  /* ── Button UI ─────────────────────────────────────────────────── */
  function setButtonActive(btn, active) {
    const outline = btn.querySelector('.icon-fav-outline');
    const fill = btn.querySelector('.icon-fav-fill');
    if (outline) outline.style.display = active ? 'none' : '';
    if (fill) fill.style.display = active ? '' : 'none';
    btn.classList.toggle('is-wishlisted', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  function syncAllButtons(productId) {
    const active = isInWishlist(productId);
    document.querySelectorAll(`.btn-favorite[data-product-id="${productId}"]`).forEach(btn => {
      setButtonActive(btn, active);
    });
  }

  function syncAllButtonsOnPage() {
    document.querySelectorAll('.btn-favorite[data-product-id]').forEach(btn => {
      setButtonActive(btn, isInWishlist(btn.dataset.productId));
    });
  }

  /* ── Toaster ───────────────────────────────────────────────────── */
  let toasterTimer = null;
  function showToaster(added) {
    const el = document.getElementById('toaster-favorite');
    if (!el) return;
    el.textContent = added
      ? (el.dataset.msgAdded || '1 produkt dodany do listy życzeń')
      : (el.dataset.msgRemoved || '1 produkt usunięty z listy życzeń');
    el.style.transform = 'translate(-50%, 0)';
    el.style.transition = 'transform 0.3s ease';
    clearTimeout(toasterTimer);
    toasterTimer = setTimeout(() => {
      el.style.transform = 'translate(-50%, -400%)';
    }, 2500);
  }

  /* ── Header wishlist: brown fill when items exist ── */
  function updateHeaderWishlistIcon() {
    const link = document.getElementById('header-wishlist-link');
    if (!link) return;
    const hasItems = getItems().length > 0;
    link.classList.toggle('header-wishlist-link--active', hasItems);
    link.setAttribute('aria-pressed', hasItems ? 'true' : 'false');
    const iconUse = link.querySelector('.header-wishlist-icon use');
    if (iconUse) {
      iconUse.setAttribute('href', hasItems ? '#icon-favorite-fill' : '#icon-favorite');
    }
  }

  function isLoggedIn() {
    return Boolean(window.__foxCustomer && window.__foxCustomer.loggedIn);
  }

  function redirectToLogin() {
    const loginUrl = (window.__foxCustomer && window.__foxCustomer.loginUrl) || '/account/login';
    const returnTo = window.location.pathname + window.location.search;
    const sep = loginUrl.includes('?') ? '&' : '?';
    window.location.href = loginUrl + sep + 'return_url=' + encodeURIComponent(returnTo);
  }

  /* ── Click handler ─────────────────────────────────────────────── */
  function handleFavoriteClick(e) {
    const btn = e.target.closest('.btn-favorite[data-product-id]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    // TZ: wishlist only for logged-in customers
    if (!isLoggedIn()) {
      redirectToLogin();
      return;
    }

    const id = btn.dataset.productId;
    const handle = btn.dataset.productHandle || null;
    const added = toggleWishlist(id, handle);

    syncAllButtons(id);
    updateHeaderWishlistIcon();
    showToaster(added);

    // On wishlist page: remove card when un-favouriting
    if (window.location.pathname.includes('/pages/wishlist') && !added) {
      const card = btn.closest('[data-wishlist-card]');
      if (card) {
        card.style.transition = 'opacity 0.3s';
        card.style.opacity = '0';
        setTimeout(() => { card.remove(); checkEmpty(); }, 300);
      }
    }
  }

  function checkEmpty() {
    const grid = document.getElementById('wishlist-grid');
    const empty = document.getElementById('wishlist-empty');
    if (!grid || !empty) return;
    if (grid.children.length === 0) {
      grid.style.display = 'none';
      empty.style.display = '';
    }
  }

  /* ── Wishlist page rendering ───────────────────────────────────── */
  async function renderWishlistPage() {
    const grid = document.getElementById('wishlist-grid');
    const empty = document.getElementById('wishlist-empty');
    const loader = document.getElementById('wishlist-loader');
    if (!grid) return;

    const items = getItems();

    if (loader) loader.style.display = 'none';

    if (items.length === 0) {
      grid.style.display = 'none';
      if (empty) empty.style.display = '';
      return;
    }

    if (empty) empty.style.display = 'none';
    grid.style.display = '';

    // Fetch each product via /products/{handle}.js
    // For items without handle, try /products.json?ids=...
    const fetches = items.map(item => {
      if (item.handle) {
        return fetch(`/products/${item.handle}.js`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null);
      }
      // Fallback for legacy items without handle: try search suggest
      return fetch(`/search/suggest.json?q=${encodeURIComponent(item.id)}&resources[type]=product&resources[limit]=1`)
        .then(r => r.json())
        .then(async data => {
          const hit = data?.resources?.results?.products?.[0];
          if (!hit?.handle) return null;
          // Load full product data via handle
          const full = await fetch(`/products/${hit.handle}.js`).then(r => r.ok ? r.json() : null);
          return full;
        })
        .catch(() => null);
    });

    const products = await Promise.all(fetches);
    grid.innerHTML = '';

    // Update handles for future use
    const updatedItems = items.map((item, i) => {
      const p = products[i];
      return p ? { id: String(item.id), handle: p.handle || item.handle } : item;
    });
    saveItems(updatedItems);

    let rendered = 0;
    products.forEach(p => {
      if (!p) return;
      grid.appendChild(buildProductCard(p));
      rendered++;
    });

    if (rendered === 0) {
      grid.style.display = 'none';
      if (empty) empty.style.display = '';
    } else {
      syncAllButtonsOnPage();
    }
  }

  function buildProductCard(p) {
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-wishlist-card', '');
    wrapper.setAttribute('data-product-id', p.id);

    // /products/{handle}.js returns price in cents
    const price = formatMoney(p.price);
    const compareAtPrice = p.compare_at_price;
    const comparePrice = compareAtPrice && compareAtPrice > p.price
      ? formatMoney(compareAtPrice) : '';
    const discount = comparePrice
      ? Math.round((compareAtPrice - p.price) / compareAtPrice * 100) : 0;

    // featured_image: object with .src on product JSON, or plain string from search
    const imageUrl = p.featured_image?.src || p.featured_image || (p.images?.[0]?.src) || '';
    const productUrl = p.url || `/products/${p.handle}`;

    wrapper.innerHTML = `
      <div class="flex flex-col s-border-t-50 s-border-r-50 s-border-b-50 s-border-l-50 s-color-border-gray-2! h-full">
        <div class="aspect-square relative">
          <a href="${productUrl}" class="block size-full">
            ${imageUrl
              ? `<img src="${imageUrl}" alt="${escHtml(p.title)}" class="size-full object-cover">`
              : '<div class="size-full bg-gray-100"></div>'}
          </a>
          <div class="absolute z-10 top-2 lg:top-3 right-2.5 lg:right-3 left-2.5 lg:left-3 pointer-events-none flex gap-2 justify-between">
            <div class="flex gap-1 lg:gap-1.5 items-start">
              ${discount > 0
                ? `<span class="pointer-events-auto block py-1 lg:py-1.5 px-2 lg:px-3 rounded-full t-very-small t-lg-very-small bg-green text-white">-${discount}%</span>`
                : ''}
            </div>
            <button
              class="btn-favorite flex pointer-events-auto is-wishlisted"
              data-product-id="${p.id}"
              data-product-handle="${p.handle || ''}"
              aria-pressed="true"
              aria-label="Usuń z listy życzeń"
            >
              <svg class="size-4 lg:size-5 icon-fav-outline" style="display:none"><use href="#icon-favorite"></use></svg>
              <svg class="size-4 lg:size-5 icon-fav-fill" style="color:var(--color-brown,#502c20)"><use href="#icon-favorite-fill"></use></svg>
            </button>
          </div>
        </div>
        <div class="flex-auto pt-2.5 px-2.5 pb-3.5 lg:p-3.5 flex flex-col">
          <a href="${productUrl}" class="block mb-2 lg:mb-2.5 t-items-name t-lg-items-name">
            <span class="line-clamp-2">${escHtml(p.title)}</span>
          </a>
          <div class="mt-auto mb-2.5 lg:mb-3 flex gap-1.5 items-center">
            <span class="t-price-cat t-lg-price-cat">${price}</span>
            ${comparePrice ? `<span class="t-price-crossed t-lg-price-crossed text-gray-2">${comparePrice}</span>` : ''}
          </div>
          <a href="${productUrl}" class="btn-primary text-center mt-auto">
            ${window.__wishlistStrings?.viewProduct || 'Zobacz produkt'}
          </a>
        </div>
      </div>`;

    return wrapper;
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    return (cents / 100).toFixed(2).replace('.', ',') + '\u00a0z\u0142';
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Init ──────────────────────────────────────────────────────── */
  function init() {
    document.addEventListener('click', handleFavoriteClick, true);
    syncAllButtonsOnPage();
    updateHeaderWishlistIcon();

    const onWishlistPage = Boolean(document.getElementById('wishlist-grid'))
      || window.location.pathname.includes('/pages/wishlist');

    if (onWishlistPage && !isLoggedIn()) {
      redirectToLogin();
      return;
    }

    if (document.getElementById('wishlist-grid')) {
      renderWishlistPage();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
